import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import usePaymentInputs from 'react-payment-inputs/es/usePaymentInputs';
import _ from 'lodash';
import { toast } from 'react-toastify';
import classes from '../script.module.scss';
import Select from '../../../../_common/Form/Selects/Select/Select';
import Modal from '../../../../_common/Modal/Modal';
import Card from './Card/Card';
import InputField from '../../../../_common/Form/TextFields/InputField';
import Button from '../../../../_common/Form/Buttons/Button';
import api from '../../../../../api/api';
import accountHelpers from '../../../../../utils/accountHelpers';
import CheckoutTable from './CheckoutTable/CheckoutTable';
import Loader from '../../../../_common/Loader/Loader';
import CheckoutCardTable from './CheckoutCardTable/CheckoutCardTable';

const claimOptions = ['Patient'];

const statuses = ['Schedule', 'QA', 'Fill', 'Shipped', 'Done'];

const cardDefaultData = {
  cardCVC: '',
  cardExpiry: '',
  cardNumber: '',
};

const PaymentModal = ({
  isOpen, patientId, onClose, setPaymentSuccessModalData,
}) => {
  const userId = accountHelpers.getUser().id;

  const {
    meta: { touchedInputs, erroredInputs }, getCVCProps, getCardNumberProps, getExpiryDateProps,
  } = usePaymentInputs();

  const [focusedField, setFocusedField] = useState('');
  const [cardData, setCardData] = useState(cardDefaultData);

  const [claim, setClaim] = useState('');
  const [checkoutData, setCheckoutData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [checkedIds, setCheckedIds] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [checkedCardIds, setCheckedCardIds] = useState('');
  const [checkoutDataLoading, setCheckoutDataLoading] = useState(false);
  const [chargeLoading, setChargeLoading] = useState(false);

  const [activeAmountId, setActiveAmountId] = useState('');

  const { cardNumber, cardExpiry, cardCVC } = cardData;

  useEffect(() => {
    (async () => {
      if (isOpen) {
        setCheckoutDataLoading(true);
        const { data } = await api.scriptCheckout({ userId, statuses, patientId });

        const selectedIds = data.response.filter((d) => d.status === 'Schedule').map((d) => d.id);
        setCheckedIds(selectedIds);

        setPaymentData(data?.authorizeNet);
        setCheckoutData(data.response);
        setCheckoutDataLoading(false);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    const checkedData = checkoutData.filter((d) => checkedIds.includes(d.id));

    const sum = _.sumBy(checkedData, (d) => (d.amount ? +parseFloat(d.amount).toFixed(2) : 0));

    setTotalAmount(+parseFloat(sum).toFixed(2));
  }, [checkedIds, checkoutData]);

  const closeModal = () => {
    onClose();
    setCheckoutData([]);
    setCheckedIds([]);
    setClaim('');
    setCardData(cardDefaultData);
  };

  const changeCardData = (path, value) => {
    setCardData((prev) => ({ ...prev, [path]: value }));
  };

  const charge = async () => {
    setChargeLoading(true);
    try {
      const info = checkedIds.map((checkedId) => {
        const { id, amount, Product } = checkoutData.find((d) => d.id === checkedId);

        return {
          claim,
          scriptId: id,
          amount: parseFloat(amount).toFixed(2),
          productId: Product?.id,
          productName: Product?.name,
          productQuantity: Product?.quantity,
        };
      });

      if (checkedCardIds) {
        const {
          profileId, customerPaymentProfileId,
        } = paymentData.find((p) => p.customerPaymentProfileId === checkedCardIds);

        const data = {
          profileId,
          paymentProfileId: customerPaymentProfileId,
          info,
          amount: parseFloat(totalAmount).toFixed(2),
        };

        const { data: { transaction } } = await api.paymentChargeCustomer(data);

        const payments = checkoutData.filter((d) => checkedIds.includes(d.id));

        setPaymentSuccessModalData({ payments, totalAmount: data.amount, transaction });
      } else {
        const data = {
          info,
          amount: parseFloat(totalAmount).toFixed(2),
          cardCVC,
          cardExpiry: cardExpiry.toString().replace(' / ', ''),
          cardNumber: cardNumber.toString().replace(/[ ]/g, ''),
        };

        const { data: { transaction } } = await api.paymentCharge(data);

        const payments = checkoutData.filter((d) => checkedIds.includes(d.id));

        setPaymentSuccessModalData({ payments, totalAmount: data.amount, transaction });
      }

      closeModal();
    } catch (e) {
      console.warn(e);
      toast.error('Something went wrong');
    }

    setChargeLoading(false);
  };

  const changeAmount = (id, value) => {
    setCheckoutData((prev) => prev.map((p) => (p.id === id ? { ...p, amount: value } : p)));
  };

  const disableChargeBtn = (totalAmount <= 0) || (_.some(erroredInputs, (value) => value) && (!checkedCardIds));

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      className={classes.payment_modal}
    >
      {checkoutDataLoading
        ? <Loader />

        : (
          <div className={classes.payment_wrapper}>
            <h2>Charge Credit Card</h2>

            <h4>Would you like to complete the purchase?</h4>

            <div className={classes.top_wrapper}>
              <CheckoutCardTable
                checkedIds={checkedCardIds}
                checkoutData={paymentData}
                setCheckedIds={setCheckedCardIds}
              />

              <div className={classes.top}>
                <div className={classes.inputs_wrapper}>
                  <InputField
                    rest={{
                      ...getCardNumberProps({ onChange: (e) => changeCardData('cardNumber', e.target.value) }),
                      maxLength: 23,
                      placeholder: '',
                      onFocus: () => setFocusedField('number'),
                    }}
                    value={cardNumber}
                    label="Card Number"
                    placeholder=""
                    onChange={() => {}}
                    wrapperClassName={classes.card_number}
                    error={touchedInputs.cardNumber && erroredInputs.cardNumber}
                    errorClassName={classes.error}
                  />

                  <div className={classes.expiry_cvc_wrapper}>
                    <InputField
                      rest={{
                        ...getExpiryDateProps({ onChange: (e) => changeCardData('cardExpiry', e.target.value) }),
                        placeholder: '',
                        onFocus: () => setFocusedField('expiry'),
                      }}
                      onChange={() => {
                      }}
                      value={cardExpiry}
                      label="Expiry Date"
                      error={touchedInputs.expiryDate && erroredInputs.expiryDate}
                      errorClassName={classes.error}
                    />

                    <InputField
                      value={cardCVC}
                      label="CVC"
                      rest={{
                        ...getCVCProps({ onChange: (e) => changeCardData('cardCVC', e.target.value) }),
                        placeholder: '',
                        onFocus: () => setFocusedField('cvc'),
                      }}
                      onChange={() => {
                      }}
                      error={touchedInputs.cvc && erroredInputs.cvc}
                      errorClassName={classes.error}
                    />
                  </div>

                  <Select
                    label="Claim"
                    options={claimOptions}
                    currentOption={claim}
                    onChange={(option) => setClaim(option)}
                    wrapperClassName={classes.claim}
                    zIndex={33}
                  />
                </div>

                <Card
                  number={cardNumber}
                  name=""
                  expiry={cardExpiry.toString().replace(' / ', '')}
                  cvc={cardCVC}
                  focused={focusedField}
                />
              </div>

            </div>

            <div className={classes.btns_wrapper}>
              <Button
                title="Cancel"
                clearButton
                onClick={closeModal}
              />

              <Button
                reverseColor
                title="Charge"
                disabled={disableChargeBtn}
                loading={chargeLoading}
                onClick={charge}
              />
            </div>

            {!!checkoutData.length && (
              <CheckoutTable
                checkedIds={checkedIds}
                totalAmount={totalAmount}
                setCheckedIds={setCheckedIds}
                checkoutData={checkoutData}
                activeAmountId={activeAmountId}
                setActiveAmountId={setActiveAmountId}
                changeAmount={changeAmount}
              />
            )}
          </div>
        )}
    </Modal>
  );
};

PaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setPaymentSuccessModalData: PropTypes.func.isRequired,
  patientId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

PaymentModal.defaultProps = {
  patientId: '',
};

export default PaymentModal;
